import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Plus,
  Pencil,
  Trash2,
  Lock,
  ArrowLeft,
  Eye,
  EyeOff,
  LayoutDashboard,
  FileText,
  Bookmark,
  FileEdit,
  Share2,
  Sparkles,
  Users,
  Building2,
  UserCheck,
  Archive,
  Wrench,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Map,
  Zap,
  BarChart3,
  ClipboardCheck,
} from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import InfraChecklist from "@/components/admin/InfraChecklist";
import AffiliatesAdminTab from "@/components/admin/AffiliatesAdminTab";
import GuestsAdminTab from "@/components/admin/GuestsAdminTab";
import DashboardTab from "@/components/admin/DashboardTab";
import InactivityReviewTab from "@/components/admin/InactivityReviewTab";
import RetentionAuditLog from "@/components/admin/RetentionAuditLog";
import PageContentEditor from "@/components/admin/PageContentEditor";
import SocialStudio from "@/components/admin/SocialStudio";
import SeasonalThemeTab from "@/components/admin/SeasonalThemeTab";
import CuratedPostsTab from "@/components/admin/CuratedPostsTab";
import PartnerApplicationsTab from "@/components/admin/PartnerApplicationsTab";
import FundingAdminTab from "@/components/admin/FundingAdminTab";
import RoadmapTab from "@/components/admin/RoadmapTab";
import BoostsAdminTab from "@/components/admin/BoostsAdminTab";
import IntelReportsTab from "@/components/admin/IntelReportsTab";
import OpportunityReviewTab from "@/components/admin/OpportunityReviewTab";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type ArticleStatus = "draft" | "in_review" | "published" | "archived";
export type Citation = { label: string; url: string; publisher?: string; accessed_at?: string };

const STATUS_LABELS: Record<ArticleStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  published: "Published",
  archived: "Archived",
};

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string;
  image_url: string | null;
  category: string;
  tags: string[];
  author: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  status: ArticleStatus;
  citations: Citation[];
}

const emptyArticle = {
  title: "",
  excerpt: "",
  body: "",
  image_url: "",
  category: "General",
  tags: [] as string[],
  author: "The Emerging Market",
  published: false,
  status: "draft" as ArticleStatus,
  citations: [] as Citation[],
};

const Admin = () => {
  const affiliatesOn = useFeatureFlag("affiliates");
  const { user, loading: authLoading } = useAuth();
  const { isSuperAdmin, loading: rolesLoading } = useRoles();
  const authenticated = !!user && isSuperAdmin;
  const [articles, setArticles] = useState<Article[]>([]);
  const [editing, setEditing] = useState<Partial<Article> | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [menuCollapsed, setMenuCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("admin-menu-collapsed") === "1";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("admin-menu-collapsed", menuCollapsed ? "1" : "0");
    }
  }, [menuCollapsed]);

  const callApi = async (action: string, article?: any) => {
    const { data, error } = await supabase.functions.invoke("manage-articles", {
      body: { action, article },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const loadArticles = async () => {
    try {
      const data = await callApi("list");
      setArticles(data.articles);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    if (authenticated) {
      loadArticles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  const handleSave = async () => {
    if (!editing?.title || !editing?.body) {
      toast.error("Title and body are required");
      return;
    }
    setLoading(true);
    try {
      const action = editing.id ? "update" : "create";
      await callApi(action, editing);
      toast.success(editing.id ? "Article updated" : "Article created");
      setEditing(null);
      await loadArticles();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this article?")) return;
    try {
      await callApi("delete", { id });
      toast.success("Article deleted");
      await loadArticles();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && editing) {
      setEditing({ ...editing, tags: [...(editing.tags || []), tagInput.trim()] });
      setTagInput("");
    }
  };

  const removeTag = (index: number) => {
    if (editing) {
      const newTags = [...(editing.tags || [])];
      newTags.splice(index, 1);
      setEditing({ ...editing, tags: newTags });
    }
  };

  if (authLoading || rolesLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        Checking access…
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent("/admin")}`} replace />;
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-card border border-border rounded-xl p-8 w-full max-w-sm text-center">
          <Lock className="h-5 w-5 text-primary mx-auto mb-3" />
          <h1 className="font-display text-xl font-bold mb-2">Admin access required</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Your account is signed in but does not have the super_admin role.
          </p>
          <Link to="/" className="text-sm text-primary hover:underline">Return home</Link>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-6 py-8 max-w-3xl">
          <Button variant="ghost" onClick={() => setEditing(null)} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Articles
          </Button>

          <h1 className="font-display text-2xl font-bold mb-6">
            {editing.id ? "Edit Article" : "New Article"}
          </h1>

          <div className="space-y-5">
            <div>
              <Label className="text-sm font-medium">Title</Label>
              <Input
                value={editing.title || ""}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="Article title"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Author</Label>
              <Input
                value={editing.author || ""}
                onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="Author name"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Category</Label>
              <Input
                value={editing.category || ""}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="e.g. Policy Analysis, Impact Story"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  className="bg-background border-border"
                  placeholder="Add a tag and press Enter"
                />
                <Button type="button" variant="outline" onClick={addTag} size="sm">Add</Button>
              </div>
              {(editing.tags?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {editing.tags?.map((tag, i) => (
                    <span key={i} className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full flex items-center gap-1">
                      {tag}
                      <button onClick={() => removeTag(i)} className="hover:text-destructive">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">Image URL</Label>
              <Input
                value={editing.image_url || ""}
                onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="https://..."
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Excerpt</Label>
              <Textarea
                value={editing.excerpt || ""}
                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                className="mt-1 bg-background border-border"
                rows={2}
                placeholder="Brief summary for article cards"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Body</Label>
              <Textarea
                value={editing.body || ""}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                className="mt-1 bg-background border-border font-mono text-sm"
                rows={16}
                placeholder="Article content (supports markdown)"
              />
            </div>

            <div className="space-y-2 pt-2">
              <Label className="text-sm font-medium">Editorial status</Label>
              <Select
                value={(editing.status as ArticleStatus) || "draft"}
                onValueChange={(v) =>
                  setEditing({ ...editing, status: v as ArticleStatus, published: v === "published" })
                }
              >
                <SelectTrigger className="bg-background border-border w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as ArticleStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Draft → In review → Published. Only “Published” is visible to readers.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Citations / Sources</Label>
              <div className="space-y-2">
                {(editing.citations ?? []).map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start">
                    <Input
                      className="col-span-4 bg-background border-border"
                      placeholder="Label (e.g. ACS 2022 5-Year)"
                      value={c.label}
                      onChange={(e) => {
                        const next = [...(editing.citations ?? [])];
                        next[i] = { ...next[i], label: e.target.value };
                        setEditing({ ...editing, citations: next });
                      }}
                    />
                    <Input
                      className="col-span-5 bg-background border-border"
                      placeholder="https://..."
                      value={c.url}
                      onChange={(e) => {
                        const next = [...(editing.citations ?? [])];
                        next[i] = { ...next[i], url: e.target.value };
                        setEditing({ ...editing, citations: next });
                      }}
                    />
                    <Input
                      className="col-span-2 bg-background border-border"
                      placeholder="Publisher"
                      value={c.publisher ?? ""}
                      onChange={(e) => {
                        const next = [...(editing.citations ?? [])];
                        next[i] = { ...next[i], publisher: e.target.value };
                        setEditing({ ...editing, citations: next });
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="col-span-1 text-destructive"
                      onClick={() => {
                        const next = [...(editing.citations ?? [])];
                        next.splice(i, 1);
                        setEditing({ ...editing, citations: next });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setEditing({
                      ...editing,
                      citations: [
                        ...(editing.citations ?? []),
                        { label: "", url: "", publisher: "", accessed_at: new Date().toISOString().slice(0, 10) },
                      ],
                    })
                  }
                >
                  <Plus className="h-4 w-4 mr-2" /> Add source
                </Button>
                <p className="text-xs text-muted-foreground">
                  Follow <span className="font-mono">docs/citation-standard.md</span>. Sources render as a numbered list at the bottom of the article.
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSave} disabled={loading} className="bg-primary text-primary-foreground">
                {loading ? "Saving..." : "Save Article"}
              </Button>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8 max-w-7xl">
        <div className="mb-6">
          <Link to="/insights" className="text-sm text-primary hover:underline mb-1 block">← Back to Insights</Link>
          <h1 className="font-display text-2xl font-bold">Admin</h1>
        </div>

        <Tabs defaultValue="dashboard" className="w-full">
          <div className="flex gap-6">
            <div className={cn("shrink-0", menuCollapsed ? "w-[52px]" : "w-[180px]")}>
              <div className="sticky top-6 space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center h-8"
                  onClick={() => setMenuCollapsed((v) => !v)}
                  aria-label={menuCollapsed ? "Expand menu" : "Collapse menu"}
                >
                  {menuCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
                <TooltipProvider delayDuration={150}>
                  <TabsList className="flex flex-col h-auto w-full bg-card border border-border rounded-xl p-2 gap-1">
                    {([
                      ["dashboard", "Dashboard", LayoutDashboard, true],
                      ["content", "Content Manager", FileText, true],
                      ["curated", "Curated Posts", Bookmark, true],
                      ["copy", "Page Copy", FileEdit, true],
                      ["social", "Social Studio", Share2, true],
                      ["seasonal", "Seasonal", Sparkles, true],
                      ["affiliates", "Affiliates", UserCheck, affiliatesOn],
                      ["partner-apps", "Partner Apps", Building2, true],
                      ["boosts", "Boosts", Zap, true],
                      ["intel-reports", "Intel Reports", BarChart3, true],
                      ["opps-review", "Opportunities", ClipboardCheck, true],
                      ["guests", "Guests", Users, true],
                      ["retention", "Retention", Archive, true],
                      ["infra", "Infrastructure", Wrench, true],
                      ["funding", "Funding", DollarSign, true],
                      ["roadmap", "Roadmap", Map, true],
                    ] as const)
                      .filter(([, , , show]) => show)
                      .map(([value, label, Icon]) => (
                        <Tooltip key={value}>
                          <TooltipTrigger asChild>
                            <TabsTrigger
                              value={value}
                              className={cn(
                                "w-full gap-2",
                                menuCollapsed ? "justify-center px-0" : "justify-start"
                              )}
                            >
                              <Icon className="h-4 w-4 shrink-0" />
                              {!menuCollapsed && <span className="truncate">{label}</span>}
                            </TabsTrigger>
                          </TooltipTrigger>
                          {menuCollapsed && <TooltipContent side="right">{label}</TooltipContent>}
                        </Tooltip>
                      ))}
                  </TabsList>
                </TooltipProvider>
              </div>
            </div>

            <div className="flex-1 min-w-0">

          <TabsContent value="dashboard">
            <DashboardTab />
          </TabsContent>

          <TabsContent value="content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold">Articles</h2>
              <Button onClick={() => setEditing({ ...emptyArticle })} className="bg-primary text-primary-foreground">
                <Plus className="h-4 w-4 mr-2" /> New Article
              </Button>
            </div>

            {articles.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p className="text-lg mb-2">No articles yet</p>
            <p className="text-sm">Create your first article to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {articles.map((article) => (
              <div
                key={article.id}
                className="bg-card border border-border rounded-xl p-5 flex items-center gap-4 hover:border-primary/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      article.published
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {article.published ? "Published" : "Draft"}
                    </span>
                    <span className="text-xs text-muted-foreground">{article.category}</span>
                  </div>
                  <h3 className="font-display font-semibold truncate">{article.title}</h3>
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground truncate mt-0.5">{article.excerpt}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing({
                        ...article,
                        tags: Array.isArray(article.tags) ? article.tags : [],
                        citations: Array.isArray(article.citations) ? article.citations : [],
                      });
                      setTagInput("");
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(article.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
            )}
          </TabsContent>

          <TabsContent value="infra">
            <InfraChecklist />
          </TabsContent>

          <TabsContent value="funding">
            <FundingAdminTab />
          </TabsContent>

          <TabsContent value="roadmap">
            <RoadmapTab />
          </TabsContent>

          <TabsContent value="copy">
            <PageContentEditor />
          </TabsContent>

          <TabsContent value="social">
            <SocialStudio />
          </TabsContent>

          <TabsContent value="seasonal">
            <SeasonalThemeTab />
          </TabsContent>

          <TabsContent value="curated">
            <CuratedPostsTab />
          </TabsContent>

          {affiliatesOn && (
            <TabsContent value="affiliates">
              <AffiliatesAdminTab />
            </TabsContent>
          )}

          <TabsContent value="partner-apps">
            <PartnerApplicationsTab />
          </TabsContent>

          <TabsContent value="boosts">
            <BoostsAdminTab />
          </TabsContent>

          <TabsContent value="intel-reports">
            <IntelReportsTab />
          </TabsContent>

          <TabsContent value="opps-review">
            <OpportunityReviewTab />
          </TabsContent>

          <TabsContent value="guests">
            <GuestsAdminTab />
          </TabsContent>

          <TabsContent value="retention">
            <InactivityReviewTab />
            <RetentionAuditLog />
          </TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
